import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { BrandsFilterParams, BrandType, BrandsListResponse, CreateBrandParams, UpdateBrandParams } from '@/types/brand'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { fetchBrands, fetchBrand, updateBrand, deleteBrand, createBrand, bulkDeleteBrands } from '@/lib/api'
import { SortEnum } from '@/enums'

// Single brand state type
type BrandStateType = {
  isLoading: boolean
  response: BrandType | null
  error: {
    status?: number
    message: string
  } | null
}

// Brands list state type
type BrandsListStateType = {
  isLoading: boolean
  response: BrandsListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type BrandsStoreState = {
  // Brands list filters state
  filters: BrandsFilterParams
  setFilters: (filters: Partial<BrandsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Brands list state
  brandsListState: BrandsListStateType
  fetchBrands: (params?: BrandsFilterParams) => Promise<BrandsListResponse>
  resetBrandsList: () => void

  // Single brand state
  brandState: BrandStateType
  getBrand: (brandId: string) => Promise<BrandType>
  createBrand: (params: CreateBrandParams) => Promise<BrandType>
  updateBrand: (brandId: string, params: UpdateBrandParams) => Promise<BrandType>
  deleteBrand: (brandId: string) => Promise<void>
  resetBrand: () => void

  // Selection operations
  selectedBrandIds: string[]
  selectBrand: (id: string) => void
  deselectBrand: (id: string) => void
  toggleBrandSelection: (id: string) => void
  selectAllBrands: (brands: BrandType[]) => void
  deselectAllBrands: () => void
  isBrandSelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteBrands: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: BrandsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'name'
}

const initialBrandState: BrandStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialBrandsListState: BrandsListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useBrandsStore = create<BrandsStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    brandState: initialBrandState,
    brandsListState: initialBrandsListState,
    selectedBrandIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof BrandsFilterParams]
          } else {
            updatedFilters[key as keyof BrandsFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: BrandsFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value as BrandsFilterParams['sortBy']
            break
          case 'createdUsers':
          case 'updatedUsers':
            filters[key] = value
            break
          default:
            filters[key as keyof BrandsFilterParams] = value as any
            break
        }
      })

      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else {
            params.set(key, value.toString())
          }
        }
      })

      return params
    },

    // Single brand operations
    getBrand: async (brandId: string) => {
      // If already loading, don't make another request
      const currentState = get().brandState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        brandState: {
          ...initialBrandState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchBrand(brandId)

        set(() => ({
          brandState: {
            ...initialBrandState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          brandState: {
            ...initialBrandState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createBrand: async (params: CreateBrandParams) => {
      set(() => ({
        brandState: {
          ...initialBrandState,
          isLoading: true
        }
      }))

      try {
        const data = await createBrand(params)

        set(() => ({
          brandState: {
            ...initialBrandState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          brandState: {
            ...initialBrandState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    updateBrand: async (brandId: string, params: UpdateBrandParams) => {
      set(() => ({
        brandState: {
          ...get().brandState,
          isLoading: true
        }
      }))

      try {
        const data = await updateBrand(brandId, params)

        set(() => ({
          brandState: {
            ...get().brandState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          brandState: {
            ...initialBrandState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteBrand: async (brandId: string) => {
      set(() => ({
        brandState: {
          ...get().brandState,
          isLoading: true
        }
      }))

      try {
        await deleteBrand(brandId)

        set(() => ({
          brandState: initialBrandState
        }))
      } catch (error: any) {
        set(() => ({
          brandState: {
            ...initialBrandState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetBrand: () => {
      set(() => ({
        brandState: initialBrandState
      }))
    },

    // Brands list operations
    fetchBrands: async (params?: BrandsFilterParams) => {
      // If already loading, don't make another request
      const currentState = get().brandsListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        brandsListState: {
          ...initialBrandsListState,
          isLoading: true
        }
      }))

      try {
        // Use provided params or fall back to store filters
        const filtersToUse = params || get().filters
        const data = await fetchBrands(filtersToUse)

        set(() => ({
          brandsListState: {
            ...initialBrandsListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          brandsListState: {
            ...initialBrandsListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetBrandsList: () => {
      set(() => ({
        brandsListState: initialBrandsListState
      }))
    },

    // Selection operations
    selectBrand: id => {
      set(state => {
        if (state.selectedBrandIds.includes(id)) {
          return state
        }
        return { selectedBrandIds: [...state.selectedBrandIds, id] }
      })
    },

    deselectBrand: id => {
      set(state => ({
        selectedBrandIds: state.selectedBrandIds.filter(brandId => brandId !== id)
      }))
    },

    toggleBrandSelection: id => {
      set(state => {
        if (state.selectedBrandIds.includes(id)) {
          return { selectedBrandIds: state.selectedBrandIds.filter(brandId => brandId !== id) }
        }
        return { selectedBrandIds: [...state.selectedBrandIds, id] }
      })
    },

    selectAllBrands: brands => {
      set(() => ({
        selectedBrandIds: brands.map(brand => brand.id)
      }))
    },

    deselectAllBrands: () => {
      set(() => ({ selectedBrandIds: [] }))
    },

    isBrandSelected: id => {
      return get().selectedBrandIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteBrands: async ids => {
      set(state => ({
        brandState: {
          ...state.brandState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteBrands({ ids })

        set(state => ({
          brandState: {
            ...state.brandState,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedBrandIds: state.selectedBrandIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          brandState: {
            ...state.brandState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default useBrandsStore
